export enum PermissionStateEnum {
  GRANTED = 'granted',
  DENIED = 'denied',
  // To see the difference between `PROMPT` vs `PROMPT_WITH_RATIONALE`, read
  // https://capacitorjs.com/docs/plugins/web#aliases for more details.
  PROMPT = 'prompt',
  PROMPT_WITH_RATIONALE = 'prompt-with-rationale',
  LIMITED = 'limited',
  // Custom state for unsupported platforms
  NOT_SUPPORTED = 'not-supported',
}

export type PermissionStateType =
  | PermissionState
  | PermissionStateEnum.PROMPT_WITH_RATIONALE
  | PermissionStateEnum.LIMITED
  | PermissionStateEnum.NOT_SUPPORTED;

export enum PermissionType {
  CAMERA = 'Camera',
  CONTACTS = 'Contacts',
  MICROPHONE = 'Microphone',
  PUSH_NOTIFICATIONS = 'Push Notifications',
  GEOLOCATION = 'Geolocation',
}

export const permissionReason = {
  CAMERA: $localize`We need this in order to take a clear picture of your face which will be used to verify your identity.`,
  CONTACTS: $localize`We need this in order to sync your contacts.`,
  VIDEO: $localize`We need access to your camera to proceed.`,
  MICROPHONE: $localize`We need access to your microphone to proceed.`,
  PHOTO: $localize`We need this in order to upload a photo for your display or cover photo.`,
  GEOLOCATION: $localize`We need this in order to help find jobs more relevant to you.`,
  PUSH_NOTIFICATIONS: $localize`We need this in order to alert you when you get new messages.`,
  VOICE_CALL: $localize`We need this in order to enable voice calling functionality.`,
  VIDEO_CALL: $localize`We need this in order to enable video calling functionality.`,
};

export enum PermissionDenyReason {
  APP = 'app',
  TIMEOUT = 'timeout',
  OTHER = 'other',
  USER_DENIED_FROM_APP = 'user',
  NOT_AVAILABLE = 'camera',
  UNSUPPORTED = 'unsupported',
}

export interface PermissionHandler {
  nativeCheck(): Promise<PermissionStateType>;

  /**
   * Request permission from the native platform.
   * @param options
   * - `redirectToSettings`: If true, redirects the user to the settings page.
   *    This is useful when the user has denied the permission since the system prompt won't be available.
   */
  nativeRequest(options: {
    redirectToSettings?: boolean;
  }): Promise<PermissionStateType>;
  webCheck(): Promise<PermissionStatus & { state: PermissionStateType }>;

  /**
   * Where the user has agreed to the internal permission request modal
   */
  internalRequestResult?(): Promise<boolean>;
}

export interface PermissionHanders {
  [PermissionType.CAMERA]: PermissionHandler;
  [PermissionType.CONTACTS]: PermissionHandler;
  [PermissionType.MICROPHONE]: PermissionHandler;
  [PermissionType.PUSH_NOTIFICATIONS]: PermissionHandler;
  [PermissionType.GEOLOCATION]: PermissionHandler;
}
